import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Layout from '../common/Layout'
import { axiosInstance } from '../../services/api'
import { getUser } from '../../utils/secureStorage'

// Shared step indicator
function StepDot({ n, active, done }) {
  const bg = done ? '#556B2F' : active ? '#E65100' : '#F2F2F2'
  const border = done ? '#556B2F' : active ? '#E65100' : '#CCCCCC'
  const color = done || active ? '#fff' : '#666666'
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
      style={{ background: bg, border: `2px solid ${border}`, color }}
      aria-label={`Step ${n}${done ? ' completed' : active ? ' current' : ''}`}
    >
      {done ? '✓' : n}
    </div>
  )
}

function StepLine({ done }) {
  return <div className="flex-1 h-0.5 mx-1" style={{ background: done ? '#556B2F' : '#CCCCCC' }} />
}

function AddMinorFlow() {
  const navigate = useNavigate()
  const { familyId: urlFamilyId } = useParams()
  const familyId = urlFamilyId || localStorage.getItem('familyId')

  const [step, setStep] = useState(1)
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [createdPerson, setCreatedPerson] = useState(null)

  // Child data — passed via location state or query params
  const [childData, setChildData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    relationship: 'child',
    gender: '',
    personId: null, // set if child already exists as a Person
  })

  const userData = getUser() || {}
  const guardianName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'You'
  const familyName = localStorage.getItem('familyName') || 'this family'

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null
    const birth = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--
    return age
  }

  const childAge = calculateAge(childData.dateOfBirth)
  const childFullName = `${childData.firstName} ${childData.lastName}`.trim()

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setChildData(prev => ({ ...prev, [name]: value }))
    if (error) setError(null)
  }

  const handleSubmit = async () => {
    if (!consent) return
    setSubmitting(true)
    setError(null)

    try {
      const response = await axiosInstance.post(`/families/${familyId}/add-minor/`, {
        firstName: childData.firstName,
        lastName: childData.lastName,
        dateOfBirth: childData.dateOfBirth || null,
        relationship: childData.relationship,
        personId: childData.personId,
        guardianConsent: true,
        consentTimestamp: new Date().toISOString(),
      })

      if (response.data.success) {
        setCreatedPerson(response.data)
        setStep(3)
      } else {
        setError(response.data.error || 'Failed to add child')
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDOB = (dob) => {
    if (!dob) return 'Not provided'
    const d = new Date(dob)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <Layout>
      <div className="min-h-full bg-[#eae9d9] flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-[440px]">
          {/* Step Indicator */}
          <div className="flex items-center mb-6" aria-label={`Step ${step} of 3`}>
            <StepDot n={1} active={step === 1} done={step > 1} />
            <StepLine done={step > 1} />
            <StepDot n={2} active={step === 2} done={step > 2} />
            <StepLine done={step > 2} />
            <StepDot n={3} active={step === 3} done={false} />
          </div>

          {/* Step 1 — Review Child Details */}
          {step === 1 && (
            <div className="bg-white rounded-xl border border-[#CCCCCC] p-6">
              <div className="text-lg font-bold text-[#333333] mb-1">Add a child to your family</div>
              <div className="text-[13px] text-[#666666] mb-4">
                You're adding a minor. You'll manage their account until they turn 16.
              </div>

              {/* Child details form */}
              <div className="bg-[#F5F5DC] rounded-[10px] p-4 mb-3.5">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#333333] mb-1">First Name *</label>
                    <input
                      name="firstName"
                      value={childData.firstName}
                      onChange={handleInputChange}
                      placeholder="Child's first name"
                      className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] rounded-lg focus:outline-none focus:border-[#556B2F]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#333333] mb-1">Last Name</label>
                    <input
                      name="lastName"
                      value={childData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last name"
                      className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] rounded-lg focus:outline-none focus:border-[#556B2F]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#333333] mb-1">Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={childData.dateOfBirth}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] rounded-lg focus:outline-none focus:border-[#556B2F]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-[#333333] mb-1">Relationship</label>
                    <select
                      name="relationship"
                      value={childData.relationship}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] rounded-lg bg-white focus:outline-none focus:border-[#556B2F]"
                    >
                      <option value="child">Son/Daughter</option>
                      <option value="grandchild">Grandchild</option>
                      <option value="niece-nephew">Niece/Nephew</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Summary card (shown when name is entered) */}
              {childData.firstName && (
                <div className="bg-[#F5F5DC] rounded-[10px] p-4 mb-3.5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-[#6B8E23] flex items-center justify-center text-xl">👦</div>
                    <div>
                      <div className="text-[15px] font-bold text-[#333333]">{childFullName || childData.firstName}</div>
                      <div className="text-xs text-[#666666]">
                        {childAge !== null ? `Age ${childAge} · ` : ''}{childData.relationship === 'child' ? 'Son/Daughter' : childData.relationship}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Relationship', childData.relationship === 'child' ? 'Son/Daughter' : childData.relationship],
                      ['Date of Birth', formatDOB(childData.dateOfBirth)],
                      ['Guardian', `${guardianName} (You)`],
                      ['Family', familyName],
                    ].map(([k, v]) => (
                      <div key={k} className="bg-white rounded-lg p-2">
                        <div className="text-[10px] text-[#999999] uppercase tracking-wider">{k}</div>
                        <div className="text-xs font-semibold text-[#333333] mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Guardian responsibilities */}
              <div className="bg-[rgba(85,107,47,0.07)] border border-[rgba(85,107,47,0.2)] rounded-[10px] p-3 mb-3.5">
                <div className="text-xs font-bold text-[#556B2F] mb-2">As guardian you can:</div>
                {[
                  `Share stories on ${childData.firstName || 'their'}'s behalf`,
                  `Control what content ${childData.firstName || 'they'} can see`,
                  `Receive time capsules addressed to ${childData.firstName || 'them'}`,
                  `Hand over the account when ${childData.firstName || 'they'} turn${childData.firstName ? 's' : ''} 16`,
                ].map(item => (
                  <div key={item} className="text-xs text-[#333333] py-0.5 flex gap-2">
                    <span className="text-[#556B2F] flex-shrink-0">✓</span>{item}
                  </div>
                ))}
              </div>

              {/* No email notice */}
              <div className="bg-[rgba(230,81,0,0.06)] border border-[rgba(230,81,0,0.25)] rounded-lg p-2.5 flex gap-2 mb-4">
                <span className="text-sm flex-shrink-0">ℹ️</span>
                <div className="text-xs text-[#7a3500] leading-relaxed">
                  <strong>No email is sent to {childData.firstName || 'them'}.</strong> Minors are added directly by their guardian.
                  {childData.firstName && ` ${childData.firstName} won't have a separate login until they're old enough.`}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3 text-xs">{error}</div>
              )}

              <button
                onClick={() => childData.firstName && setStep(2)}
                disabled={!childData.firstName}
                className="w-full py-3 rounded-lg font-bold text-[13px] text-white transition-colors disabled:cursor-not-allowed"
                style={{ background: childData.firstName ? '#556B2F' : '#bbb' }}
              >
                Continue →
              </button>
            </div>
          )}

          {/* Step 2 — Guardian Consent */}
          {step === 2 && (
            <div className="bg-white rounded-xl border border-[#CCCCCC] p-6">
              <div className="text-lg font-bold text-[#333333] mb-1">
                Confirm you're {childData.firstName}'s guardian
              </div>
              <div className="text-[13px] text-[#666666] mb-4">
                By adding {childData.firstName}, you confirm you have parental or legal guardian rights to manage their account.
              </div>

              {/* Visual connector: Guardian → Child */}
              <div className="bg-[#F5F5DC] rounded-[10px] p-4 mb-4">
                <div className="flex items-center justify-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-[#556B2F] flex items-center justify-center text-white font-bold text-sm">
                    {guardianName[0]?.toUpperCase() || 'G'}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-0.5 bg-[#6B8E23]" />
                    <span className="text-[11px] font-bold text-[#556B2F] whitespace-nowrap">Guardian of</span>
                    <div className="w-6 h-0.5 bg-[#6B8E23]" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#6B8E23] flex items-center justify-center text-xl">👦</div>
                </div>
                <div className="text-center text-xs text-[#666666] mt-2.5">
                  <strong>{guardianName}</strong> will manage {childFullName}'s account
                </div>
              </div>

              {/* Consent checkbox */}
              <label className="flex items-start gap-2.5 cursor-pointer mb-5" htmlFor="guardian-consent">
                <input
                  type="checkbox"
                  id="guardian-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0"
                  style={{ accentColor: '#556B2F' }}
                />
                <span className="text-xs text-[#333333] leading-relaxed">
                  I confirm I am the parent or legal guardian of {childFullName} and have the right to create
                  and manage their account on One Generation to Next.
                </span>
              </label>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3 text-xs">{error}</div>
              )}

              <div className="flex gap-2.5">
                <button
                  onClick={() => { setStep(1); setConsent(false); }}
                  className="px-4 py-2.5 text-[13px] font-bold text-[#666666] border border-[#666666] rounded-lg bg-transparent hover:bg-gray-50 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={() => consent && handleSubmit()}
                  disabled={!consent || submitting}
                  className="flex-1 py-2.5 text-[13px] font-bold text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                  style={{ background: consent && !submitting ? '#556B2F' : '#bbb' }}
                >
                  {submitting ? 'Adding...' : `Add ${childData.firstName} to Family`}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Done */}
          {step === 3 && (
            <div className="bg-white rounded-xl border border-[#CCCCCC] p-6 text-center">
              <div className="text-[52px] mb-3">👨‍👩‍👦</div>
              <div className="text-[22px] font-bold text-[#333333] mb-2">{childData.firstName} is in the family!</div>
              <div className="text-[13px] text-[#666666] mb-5 leading-relaxed">
                <strong>{childFullName}</strong> has been added to <strong>{familyName}</strong>.<br />
                You can share stories on their behalf.
              </div>

              <div className="bg-[#F5F5DC] rounded-[10px] p-4 mb-3.5 text-left">
                <div className="text-xs font-bold text-[#556B2F] mb-2.5">What you can do now:</div>
                {[
                  ['📖', `Share a story on ${childData.firstName}'s behalf`],
                  ['📸', `Upload photos and tag ${childData.firstName}`],
                  ['⏰', `Create a time capsule for when ${childData.firstName} is older`],
                  ['🌳', `View ${childData.firstName} in the family tree`],
                ].map(([icon, text]) => (
                  <div key={text} className="text-xs text-[#333333] py-1 flex gap-2">
                    <span>{icon}</span>{text}
                  </div>
                ))}
              </div>

              {/* Birthday reminder notice */}
              <div className="bg-[rgba(85,107,47,0.07)] border border-[rgba(85,107,47,0.25)] rounded-lg p-2.5 flex gap-2 mb-4 text-left">
                <span className="text-sm flex-shrink-0">🔔</span>
                <div className="text-xs text-[#2d6a00] leading-relaxed">
                  We'll remind you when {childData.firstName} turns 16 so you can hand over control of their account directly to them.
                </div>
              </div>

              <button
                onClick={() => navigate('/family-hub')}
                className="w-full bg-[#556B2F] text-white py-3 rounded-lg font-bold text-[13px] hover:bg-[#4A5D23] transition-colors"
              >
                Go to My Family →
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default AddMinorFlow
